<script setup>
import { computed } from 'vue'
import DotsLoader from '@/components/Primitives/Loaders/DotsLoader.vue'

/**
 * @component ActionButton
 * @description Pulsante primario con stato loading, icona Material e varianti di dimensione.
 * Durante il loading il contenuto resta nel DOM (visibility hidden) per mantenere le dimensioni
 * del pulsante; il DotsLoader viene sovrapposto in posizione assoluta.
 *
 * @prop {String}          title         - Testo del pulsante (usato anche come attributo title e come fallback al slot)
 * @prop {String|Array|Object} customClass - Classi CSS aggiuntive da applicare al root <button>
 * @prop {Boolean}         loading       - true = mostra il loader e blocca i click
 * @prop {Boolean}         disabled      - true = pulsante non interagibile
 * @prop {String}          icon          - Nome icona Material Icons (es. 'download', 'edit'). Stringa vuota = nessuna icona
 * @prop {String}          iconPosition  - Posizione icona: 'left'|'right' (centrato con testo) | 'full-left'|'full-right' (icona a un estremo, testo centrato)
 * @prop {String}          size          - Dimensione: ''=full-width (default retrocompatibile) | 'small' | 'medium' | 'large'
 * @prop {String}          shape         - Forma del pulsante: ''=eredita da customClass | 'squared' | 'rounded' (pill). Default '' per retrocompatibilità.
 * @prop {String}          color         - Colore del pulsante dalla palette condivisa: ''=eredita da customClass | 'red' | 'green' | 'yellow' | 'navy' | 'acqua' | 'gray'. Default ''.
 *
 * @emits click - Emesso al click solo se non loading e non disabled
 *
 * @slot default - Testo del pulsante; se omesso viene usato il valore di `title`
 */

const emit = defineEmits(['click'])

const props = defineProps({
  title: {
    type: String,
    default: 'Go'
  },
  customClass: {
    type: [String, Array, Object],
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  icon: {
    type: String,
    default: ''
  },
  iconPosition: {
    type: String,
    default: 'left',
    validator: v => ['left', 'right', 'full-left', 'full-right'].includes(v)
  },
  size: {
    type: String,
    default: '',
    validator: v => ['', 'small', 'medium', 'large'].includes(v)
  },
  shape: {
    type: String,
    default: '',
    validator: v => ['', 'squared', 'rounded'].includes(v)
  },
  color: {
    type: String,
    default: '',
    validator: v => ['', 'red', 'green', 'yellow', 'navy', 'acqua', 'gray'].includes(v)
  },
})

const isLoading = computed(() => props.loading)
// → Boolean: true se icon e posizione sono 'left' o 'full-left'; determina se renderizzare l'icona prima del testo
const iconOnLeft = computed(() => props.icon && (props.iconPosition === 'left' || props.iconPosition === 'full-left'))
// → Boolean: true se icon e posizione sono 'right' o 'full-right'; determina se renderizzare l'icona dopo il testo
const iconOnRight = computed(() => props.icon && (props.iconPosition === 'right' || props.iconPosition === 'full-right'))

function onClick() {
  if (isLoading.value || props.disabled) return
  emit('click')
}
</script>

<template>
  <button
    @click="onClick"
    :class="[
      customClass,
      `ab-btn--${size || 'full'}`,
      shape && `ab-btn--${shape}`,
      color && `ab-btn--color-${color}`,
      { 'is-disabled': isLoading || disabled }
    ]"
    :title="title"
    :disabled="isLoading || disabled"
  >
    <!-- loader sovrapposto: il contenuto resta visibile (visibility hidden) per mantenere le dimensioni -->
    <span v-if="isLoading" class="ab-loader">
      <DotsLoader color="white" />
    </span>
    <span
      class="ab-content"
      :class="[`ab-content--${iconPosition}`, { 'ab-content--hidden': isLoading }]"
    >
      <span v-if="iconOnLeft" class="material-icons ab-icon">{{ icon }}</span>
      <span class="ab-text"><slot>{{ title }}</slot></span>
      <span v-if="iconOnRight" class="material-icons ab-icon">{{ icon }}</span>
    </span>
  </button>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";

button {
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

button:disabled {
  border-color: $grayDisabled;
  background: $bgDisabled !important;
  cursor: not-allowed;
  opacity: 0.7;
}

/* dimensioni: default full-width (retrocompatibile) */
.ab-btn--full {
  width: 100%;
}

/* size variants: si adattano al contenuto con padding */
.ab-btn--small  { width: auto; padding: 6px 14px;  font-size: $fontSmall; }
.ab-btn--medium { width: auto; padding: 10px 20px; font-size: $font14; }
.ab-btn--large  { width: auto; padding: 14px 28px; font-size: $fontMiddle; }

/* loader centrato in overlay, non sposta il layout */
.ab-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* il contenuto resta nel DOM (mantiene le dimensioni), solo nascosto visivamente */
.ab-content--hidden {
  visibility: hidden;
}

.ab-content {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.ab-content--left,
.ab-content--right {
  justify-content: center;
}

.ab-content--full-left {
  justify-content: flex-start;
}

.ab-content--full-right {
  justify-content: flex-end;
}

.ab-content--full-left .ab-text,
.ab-content--full-right .ab-text {
  flex: 1;
  text-align: center;
}

/* shape variants */
.ab-btn--squared { border-radius: 0; }
.ab-btn--rounded { border-radius: 999px; }

/* color variants — fonte: $pal-* in variables.scss, stesse tonalità di ColoredLabels */
/* Testo bianco per sfondi scuri/medi; testo navy per yellow e gray (bassa contrasto su bianco) */
.ab-btn--color-red    { background-color: hsl($pal-red-h,    $pal-red-s,    $pal-red-l);    color: white;    box-shadow: inset 0 0 0 0 rgba(0,0,0,0), 0 2px 6px rgba(0,0,0,0.10); transition: box-shadow 0.25s ease; }
.ab-btn--color-green  { background-color: hsl($pal-green-h,  $pal-green-s,  $pal-green-l);  color: white;    box-shadow: inset 0 0 0 0 rgba(0,0,0,0), 0 2px 6px rgba(0,0,0,0.10); transition: box-shadow 0.25s ease; }
.ab-btn--color-yellow { background-color: hsl($pal-yellow-h, $pal-yellow-s, $pal-yellow-l); color: $bluFour; box-shadow: inset 0 0 0 0 rgba(0,0,0,0), 0 2px 6px rgba(0,0,0,0.10); transition: box-shadow 0.25s ease; }
.ab-btn--color-navy   { background-color: hsl($pal-navy-h,   $pal-navy-s,   $pal-navy-l);   color: white;    box-shadow: inset 0 0 0 0 rgba(0,0,0,0), 0 2px 6px rgba(0,0,0,0.10); transition: box-shadow 0.25s ease; }
.ab-btn--color-acqua  { background-color: hsl($pal-acqua-h,  $pal-acqua-s,  $pal-acqua-l);  color: white;    box-shadow: inset 0 0 0 0 rgba(0,0,0,0), 0 2px 6px rgba(0,0,0,0.10); transition: box-shadow 0.25s ease; }
.ab-btn--color-gray   { background-color: hsl($pal-gray-h,   $pal-gray-s,   $pal-gray-l);   color: $bluFour; box-shadow: inset 0 0 0 0 rgba(0,0,0,0), 0 2px 6px rgba(0,0,0,0.10); transition: box-shadow 0.25s ease; }

/* hover: overlay scuro che si espande dal bordo verso il centro, shadow mantenuta */
.ab-btn--color-red:not(:disabled):hover,
.ab-btn--color-green:not(:disabled):hover,
.ab-btn--color-yellow:not(:disabled):hover,
.ab-btn--color-navy:not(:disabled):hover,
.ab-btn--color-acqua:not(:disabled):hover,
.ab-btn--color-gray:not(:disabled):hover { box-shadow: inset 0 0 0 100px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.10); }

@media (prefers-reduced-motion: reduce) {
  .ab-btn--color-red, .ab-btn--color-green, .ab-btn--color-yellow,
  .ab-btn--color-navy, .ab-btn--color-acqua, .ab-btn--color-gray { transition: none; }
}
</style>